import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountPaymentService, IFirebaseAddress } from '@ztarmobile/zwp-service-backend';
import { AppState } from 'src/app/app.service';
import { AddressesAutocompleteService, IAddress, IAutoCompletePrediction } from '@ztarmobile/zwp-service-backend-v2';
import { debounceTime, distinctUntilChanged, Observable, of, Subscription, switchMap } from 'rxjs';
import { NAME_PATTERN } from 'src/app/app.config';
import { AddressAutoCompleteHelperService } from '@services/address-auto-complete.service';

// `IFirebaseAddress` and `IAddress` only conflict on `createdAt` (number vs string),
// so we reuse `IAddress` minus that field as the shared shape the widget reads and emits.
export type AddressLookupValue = Omit<IAddress, 'createdAt'>;

@Component({
  selector: 'app-address-lookup',
  templateUrl: 'address-lookup.html',
  styleUrls: ['address-lookup.component.scss']
})
export class AddressLookupComponent implements OnDestroy, OnInit, OnChanges {
  public static ADDRESS_DETAILS_FIELD = {
    COUNTRY: 'COUNTRY',
    CITY: 'CITY',
    STATE: 'STATE',
    POSTAL: 'POSTAL'
  };

  @Input() displayedAddress: IFirebaseAddress | IAddress;
  @Input() showAlias = false;
  @Input() readonly = false;
  @Input() billingAddressAlias = false;
  @Input() touchForm = false;
  @Input() resetForm = false;
  @Input() populateForm = false;
  @Output() addressChange: EventEmitter<AddressLookupValue> = new EventEmitter();
  @Output() isValid: EventEmitter<boolean> = new EventEmitter();

  public namePattern= NAME_PATTERN;
  public displayedAddressModel: AddressLookupValue = {} as AddressLookupValue;
  public addressFieldsForm: FormGroup;
  public hasDetails = false;
  public isValidCity = true;
  public isValidState = true;
  public isValidPostal = true;
  public isValidAlias = true;
  public filteredOptions: Observable<Array<IAutoCompletePrediction>>;
  public filteredOptionsSubscription: Subscription;
  public isValidName: boolean;

  private streetSearchText: string;
  constructor(private cdRef: ChangeDetectorRef, private placesAutoCompleteService: AddressesAutocompleteService,
              private formBuilder: FormBuilder, private appState: AppState,
    private addressAutoCompleteService: AddressAutoCompleteHelperService
  ) {
    this.addressFieldsForm = formBuilder.group({
      alias: ['', Validators.pattern(this.namePattern)],
      address1: ['', Validators.compose([Validators.required, this.addressAutoCompleteService.addressObjectValidator])],
      address2: [''],
      country: [''],
      city: ['', Validators.compose([Validators.required, Validators.pattern(/^[a-zA-Z][a-zA-Z\.\s]*$/)])],
      state: ['', Validators.compose([Validators.required, Validators.pattern(/^[a-zA-Z][a-zA-Z\s]*$/)])],
      postalCode: ['', Validators.compose([Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)])],
    });
    this.hasDetails = false;
    this.displayedAddressModel = this.displayedAddress;
    // eslint-disable-next-line max-len
    this.addressFieldsForm.valueChanges.subscribe((formValue) => this.isValid.emit(this.addressFieldsForm.valid &&
      this.validateCityName(formValue.city) && this.validateStateName(formValue.state) &&
      this.validatePostalCode(formValue.postalCode)
      && this.validateAlias(formValue.alias) && this.validateAddress1(formValue.address1)));
  }

  ngOnInit(): void {
    this.cdRef.detectChanges();
    if (!!this.showAlias) {
      this.addressFieldsForm.controls.alias.setValidators([Validators.required, Validators.pattern(this.namePattern)]);
      this.addressFieldsForm.controls.alias.updateValueAndValidity();
    } else {
      this.addressFieldsForm.controls.alias.clearValidators();
      this.addressFieldsForm.controls.alias.updateValueAndValidity();
    }
    if (!!this.populateForm) {
      this.addressFieldsForm.controls.alias.setValue(!!this.displayedAddress.name ? this.displayedAddress.name : this.displayedAddress.alias);
      this.addressFieldsForm.controls.address1.setValue(this.displayedAddress.address1);
      this.addressFieldsForm.controls.address2.setValue(this.displayedAddress.address2);
      this.addressFieldsForm.controls.city.setValue(this.displayedAddress.city);
      this.addressFieldsForm.controls.state.setValue(this.displayedAddress.state);
      this.addressFieldsForm.controls.postalCode.setValue(this.displayedAddress.postalCode);
      this.addressFieldsForm.controls.country.setValue(this.displayedAddress.country);
      this.displayedAddressModel = Object.assign({}, this.displayedAddress);
    }

    const sessionId = sessionStorage.getItem('placesSessionToken');
    this.filteredOptions = this.addressFieldsForm.controls.address1.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value !== 'string' || value.trim().length === 0) {
          return of([]); // Clear the autocomplete suggestions
        }

        return this.placesAutoCompleteService.findAddress(value, sessionId);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.displayedAddress) {
      const address: AddressLookupValue = changes.displayedAddress.currentValue;
      this.hasDetails = !!address && !!address.postalCode;
      if (!!address) {
        if (!address.main_text) {
          address.main_text = `${!!address.address1 ? address.address1 : ''}`;
        }
        this.fillAddress(address)
      }
    }
    if (!!changes.touchForm && !!changes.touchForm.currentValue) {
      this.addressFieldsForm.markAllAsTouched();
    }
    if (!!changes.resetForm && !!changes.resetForm.currentValue) {
      this.addressFieldsForm.reset();
    }
  }

  ngOnDestroy(): void {
    this.cdRef.detach();
    this.filteredOptionsSubscription?.unsubscribe();
  }
  
  public fillAddress(address, afterApi?:boolean) {
    if (!afterApi) {
      this.addressFieldsForm.controls.address1.setValue(address.address1);
    }
    this.addressFieldsForm.controls.address2.setValue(address.address2);
    this.addressFieldsForm.controls.city.setValue(address.city);
    this.addressFieldsForm.controls.state.setValue(address.state);
    this.addressFieldsForm.controls.postalCode.setValue(address.postalCode);
    this.addressFieldsForm.controls.country.setValue(address.country);
    if (address.address1) {
      this.displayedAddressModel = Object.assign({}, address);
    }
  }
  
  public displayFn(option: IAutoCompletePrediction): string {
    return typeof option === 'string' ? option : option?.description || '';
  }

  public addressSelected(selectedAddress: IAutoCompletePrediction): void {
    this.appState.loading = true;

    this.addressAutoCompleteService
      .addressDetails(selectedAddress, this.displayedAddressModel, selectedAddress.main_text)
      .subscribe({
        next: (result) => {
          this.streetSearchText = !!result.model.address1 && result.model.address1.length > 0 ? result.model.address1 : null;
          this.fillAddress(result.model, true);
          this.addressFieldChanged();
          this.appState.loading = false;
        },
        error: (err) => {
          this.appState.loading = false;
          console.warn('Failed to resolve address details', err);
          this.getAddressValues(this.displayedAddressModel, this.displayedAddressModel.name)
        }
      });
  }

  public addressFieldChanged(): void {
    if (!this.displayedAddressModel) {
      this.displayedAddressModel = {} as AddressLookupValue
    }
    this.displayedAddressModel.name = this.addressFieldsForm.get('alias').value;
    this.displayedAddressModel.address2 = this.addressFieldsForm.get('address2').value;
    this.displayedAddressModel.city = this.addressFieldsForm.get('city').value;
    this.displayedAddressModel.state = this.addressFieldsForm.get('state').value;
    this.displayedAddressModel.postalCode = this.addressFieldsForm.get('postalCode').value;
    this.displayedAddressModel.address1 = AccountPaymentService.shortenAddress(this.addressFieldsForm.controls.address1.value, 20);
    this.addressChange.emit(this.getAddressValues(this.displayedAddressModel));
  }

  public addressLookupChanged(value): void {
    this.displayedAddressModel.address1 = this.addressFieldsForm.get('address1').value;
    this.addressChange.emit(this.getAddressValues(this.displayedAddressModel, value));
  }

  public validateAlias(alias): boolean {
    if (!!this.showAlias) {
      this.isValidName = /^[A-Za-z]+[ \t]?[A-Za-z- ]+?[ \t]*$/.test(alias);
      return this.isValidName;
    } else {
      return true;
    }
  }
  public validateAddress1(name): boolean {
    if (!!name || !!this.displayedAddress.address1) {
      return true;
    } else {
      return false;
    }
  }
  public validateCityName(name: string): boolean {
    this.isValidCity = /^[a-zA-Z][a-zA-Z\.\s]*$/.test(name);
    return this.isValidCity;
  }
  public validateStateName(name: string): boolean {
    this.isValidState = /^[a-zA-Z][a-zA-Z\s]*$/.test(name);
    return this.isValidState;
  }

  public validatePostalCode(code: string): boolean {
    this.isValidPostal = /^\d{5}(-\d{4})?$/.test(code);
    return this.isValidPostal;
  }
  private getAddressValues(placeInfo: any, searchTerms?: string): AddressLookupValue {
    let address: AddressLookupValue = {
      name: !!this.displayedAddressModel.name ? this.displayedAddressModel.name : this.displayedAddressModel.alias,
      address1: placeInfo.address1,
      address2: placeInfo.address2,
      state: placeInfo.state ? placeInfo.state.toUpperCase() : placeInfo.state,
      city: placeInfo.city,
      country: placeInfo.country || 'United States',
      postalCode: placeInfo.postalCode
    } as AddressLookupValue;

    if (!!this.displayedAddressModel.id) {
      address.id = this.displayedAddressModel.id;
      address.isDefault = this.displayedAddressModel.isDefault;
    }
    if (!!searchTerms && typeof searchTerms === 'string') {
      if (!!this.streetSearchText) {
        if (!searchTerms.match(this.streetSearchText)) {
          this.streetSearchText = null;
        }
      }
      address.address1 = !!this.streetSearchText ? address.address1 : searchTerms.trim();
    }

    // Clean Out empty values so they dont get assigned to Credit card info,
    address = this.removeEmptyValues(address);
    return Object.assign({}, address);
  }

  private removeEmptyValues(obj): any {
    Object.keys(obj).forEach((key) => {
      if (obj[key] && typeof obj[key] === 'object') {
        this.removeEmptyValues(obj[key]);
      } else if (obj[key] == null || obj[key] === '') {
        delete obj[key];
      }
    });
    return obj;
  }
}
